/**
 * Notes on one Sales Insights account: read the newest, and add one.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AccountNoteView } from "@onsemble/shared/types/sales-insights";
import { useApiRequest, useAuthenticatedQuery } from "@/hooks/use-api";
import { pcInsightsAccountNotesQueryKey } from "@/requests/pc-insights";

/** The account's newest notes, newest first */
export const useAccountNotes = (accountId: string) =>
  useAuthenticatedQuery<AccountNoteView[]>([...pcInsightsAccountNotesQueryKey(accountId)]);

/** Save a note, then refetch the list so it shows at the top */
export const useAddAccountNote = (accountId: string) => {
  const queryClient = useQueryClient();
  const { apiRequest } = useApiRequest();

  return useMutation({
    // apiRequest throws on any non-2xx response, so failures reach onError
    mutationFn: async (body: string): Promise<AccountNoteView> => {
      const response = await apiRequest(
        "POST",
        pcInsightsAccountNotesQueryKey(accountId)[0],
        { body },
      );
      return response.json();
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: pcInsightsAccountNotesQueryKey(accountId) }),
  });
};
